import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Copy, Image as ImageIcon, MessageCircle, MoreHorizontal, Pencil, Reply, Send, SmilePlus, Trash2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import liveChatApi, {
  centralChatTicketsEnabled,
  createLiveChatSocketTicket,
  liveChatWsBaseUrl,
} from '../../../services/liveChatApi';
import { getStoredSessionToken } from '../../../services/sessionTokens';
import { useTenantTheme } from '../../../contexts/TenantThemeContext';
import { NEON } from './playerHelpers';
import SafeImage from '../../../components/SafeImage';
import { buildPlayerReadAcknowledgement, shouldRefreshReadForSocketMessage } from '../playerChatReadState';
import { chatAgentIdentityKey, sameChatAgent } from '../playerChatAgents';

const IPHONE_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😍', '😘', '😗', '😙', '😚',
  '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️',
  '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰',
  '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱',
  '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👻', '💀',
  '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️',
  '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🙏', '✍️', '💪', '🫶', '❤️', '🧡', '💛', '💚',
  '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🔥', '💯', '✨',
  '⭐', '🌟', '💫', '🎉', '🎊', '🎁', '🏆', '🥇', '⚡', '💥', '💦', '💨', '👀', '🧠', '💎', '🍀', '🌹', '🌈'
];
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🙏', '🔥'];
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const LINK_RE = /((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,!?;:'"\]])/gi;
const LOCAL_PLAYER_DEMO_TOKEN = 'demo_player_chat';
const LOCAL_DEMO_CHAT_KEY = 'white_room_demo_chat_messages';
const LOCAL_DEMO_CHAT_EVENT = 'white-room-demo-chat-updated';
const LOCAL_DEMO_BRIDGE_URL = 'http://127.0.0.1:3020/messages';

const isLocalPlayerDemoChat = () => (
  typeof window !== 'undefined'
  && ['127.0.0.1', 'localhost'].includes(window.location.hostname)
  && getStoredSessionToken({ tenant: 'demo', portal: 'player' }) === LOCAL_PLAYER_DEMO_TOKEN
);

const demoTimestamp = (minutesAgo = 0) => new Date(Date.now() - minutesAgo * 60000).toISOString();
const demoConversationForAgent = (agent = {}) => ({
  id: 'demo-conversation-1',
  tenant_id: 'demo-tenant',
  player_id: 'demo-player-1',
  subject: agent?.name ? `Texted to agent ${agent.name}` : 'Texted to agent ANNA',
  preferred_agent_id: agent?.id || 'demo-agent-anna',
  preferred_agent_name: agent?.name || 'ANNA',
  preferred_agent_title: agent?.title || 'Live Agent',
  preferred_agent_avatar_url: agent?.avatar_url || '',
});
const defaultDemoPlayerMessages = (agent = {}) => [
  {
    id: 'demo-player-msg-1',
    conversation_id: 'demo-conversation-1',
    tenant_id: 'demo-tenant',
    sender_id: 'demo-player-1',
    sender_name: 'Emily Demo',
    sender_role: 'player',
    body: 'Hello',
    attachments: [],
    created_at: demoTimestamp(12),
    read_by_player: true,
    read_by_staff: true,
    target_agent_name: agent?.name || 'ANNA',
  },
  {
    id: 'demo-staff-msg-1',
    conversation_id: 'demo-conversation-1',
    tenant_id: 'demo-tenant',
    sender_id: 'demo-staff-chat',
    sender_name: agent?.name || 'ANNA',
    sender_role: 'staff',
    body: 'Hello you wanna play',
    attachments: [],
    created_at: demoTimestamp(10),
    read_by_player: true,
    read_by_staff: true,
    target_agent_name: agent?.name || 'ANNA',
    target_agent_title: agent?.title || 'Live Agent',
    target_agent_avatar_url: agent?.avatar_url || '',
  },
  {
    id: 'demo-player-msg-2',
    conversation_id: 'demo-conversation-1',
    tenant_id: 'demo-tenant',
    sender_id: 'demo-player-1',
    sender_name: 'Emily Demo',
    sender_role: 'player',
    body: 'I wanna load',
    attachments: [],
    created_at: demoTimestamp(4),
    read_by_player: true,
    read_by_staff: false,
    target_agent_name: agent?.name || 'ANNA',
  },
];

const readDemoMessages = (agent = {}) => {
  try {
    const stored = JSON.parse(localStorage.getItem(LOCAL_DEMO_CHAT_KEY) || '[]');
    if (Array.isArray(stored) && stored.length) return stored;
  } catch {}
  const seeded = defaultDemoPlayerMessages(agent);
  try { localStorage.setItem(LOCAL_DEMO_CHAT_KEY, JSON.stringify(seeded)); } catch {}
  return seeded;
};

const writeDemoMessages = (updater, agent = {}) => {
  const current = readDemoMessages(agent);
  const next = typeof updater === 'function' ? updater(current) : updater;
  try { localStorage.setItem(LOCAL_DEMO_CHAT_KEY, JSON.stringify(next)); } catch {}
  try {
    fetch(LOCAL_DEMO_BRIDGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(() => {});
  } catch {}
  window.dispatchEvent(new Event(LOCAL_DEMO_CHAT_EVENT));
  try { new BroadcastChannel(LOCAL_DEMO_CHAT_EVENT).postMessage({ type: 'updated' }); } catch {}
  return next;
};

const syncDemoMessagesFromBridge = async (agent = {}) => {
  try {
    const res = await fetch(LOCAL_DEMO_BRIDGE_URL, { cache: 'no-store' });
    if (!res.ok) return readDemoMessages(agent);
    const data = await res.json();
    if (Array.isArray(data.messages)) {
      localStorage.setItem(LOCAL_DEMO_CHAT_KEY, JSON.stringify(data.messages));
      return data.messages;
    }
  } catch {}
  return readDemoMessages(agent);
};

const parseChatDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value);
  const normalized = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatChatTime = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(date);
};

const localDateKey = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  return [date.getFullYear(), date.getMonth(), date.getDate()].join('-');
};

const dateSeparatorLabel = (value, previousValue) => {
  const currentKey = localDateKey(value);
  if (!currentKey || currentKey === localDateKey(previousValue)) return '';
  const date = parseChatDate(value);
  const now = new Date();
  const todayKey = localDateKey(now);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (currentKey === todayKey) return 'Today';
  if (currentKey === localDateKey(yesterday)) return 'Yesterday';
  return new Intl.DateTimeFormat([], {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date);
};

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) {
    try {
      const parsed = new URL(url);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${window.location.origin}${parsed.pathname}${parsed.search || ''}`;
      }
      if (parsed.pathname.startsWith('/api/chat/media/')) {
        return `${window.location.origin}${parsed.pathname}${parsed.search || ''}`;
      }
    } catch {
      return url;
    }
    return url;
  }
  if (url.startsWith('/api/chat/media/')) return url;
  if (url.startsWith('/api/uploads/')) return url;
  if (url.startsWith('/uploads/')) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const liveChatSocketUrl = (path) => {
  if (liveChatWsBaseUrl) return `${liveChatWsBaseUrl}${path}`;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
};

const messageListSignature = (items = []) => items.map(item => [
  item.id,
  item.client_message_id || '',
  item.body,
  item.deleted ? 'deleted' : '',
  item.edited_at || '',
  item.reply_to_message_id || item.reply_to?.id || '',
  item.read_by_staff ? 'staff-read' : '',
  item.read_by_player ? 'player-read' : '',
  item.attachments?.length || 0,
].join(':')).join('|');

const mergeChatMessage = (items = [], incoming, limit = 60) => {
  if (!incoming?.id) return items;
  const clientId = incoming.client_message_id || '';
  let merged = false;
  const next = items.map(item => {
    const sameServerMessage = item.id === incoming.id;
    const samePendingMessage = clientId && item.client_message_id === clientId;
    if (sameServerMessage || samePendingMessage) {
      merged = true;
      return incoming;
    }
    return item;
  });
  if (!merged) next.push(incoming);

  const seen = new Set();
  const deduped = [];
  for (let i = next.length - 1; i >= 0; i -= 1) {
    const item = next[i];
    const key = item.id && !String(item.id).startsWith('temp_')
      ? `id:${item.id}`
      : item.client_message_id
        ? `client:${item.client_message_id}`
        : `temp:${item.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.unshift(item);
  }
  return deduped.slice(-limit);
};

const AGENT_SUBJECT_PREFIX = 'Texted to agent ';
const agentNameFromSubject = (subject = '') => {
  if (typeof subject !== 'string' || !subject.startsWith(AGENT_SUBJECT_PREFIX)) return '';
  return subject.slice(AGENT_SUBJECT_PREFIX.length).trim();
};

const parseAgentTagFromBody = (body = '') => {
  if (typeof body !== 'string' || !body.startsWith(AGENT_SUBJECT_PREFIX)) {
    return { agentName: '', body: body || '' };
  }
  const [firstLine, ...rest] = body.split('\n');
  const agentName = firstLine.slice(AGENT_SUBJECT_PREFIX.length).trim();
  const cleanBody = rest.join('\n').replace(/^\n/, '');
  return { agentName, body: cleanBody };
};

const messagePreviewBody = (msg = {}) => (
  parseAgentTagFromBody(msg.body || '').body
  || (msg.attachments?.length ? 'Photo' : '')
);

const normalizeSupportLabel = (value = '') => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const GENERIC_SUPPORT_LABELS = new Set(['support', 'support team', 'support agent', 'live support', 'live agent', 'live agents']);
const chatSubtitleForAgent = (name = '', title = '', fallback = 'Live Agents') => {
  const cleanName = String(name || '').trim();
  const cleanTitle = String(title || '').trim();
  if (!cleanName) return fallback;
  if (!cleanTitle) return '';
  const normalizedName = normalizeSupportLabel(cleanName);
  const normalizedTitle = normalizeSupportLabel(cleanTitle);
  if (normalizedName === normalizedTitle) return '';
  if (GENERIC_SUPPORT_LABELS.has(normalizedName) && GENERIC_SUPPORT_LABELS.has(normalizedTitle)) return '';
  return cleanTitle;
};

const replyPreviewForMessage = (msg = {}) => ({
  id: msg.id,
  sender_id: msg.sender_id,
  sender_name: msg.sender_name || (msg.sender_role === 'player' ? 'You' : 'Support'),
  sender_role: msg.sender_role,
  body: messagePreviewBody(msg).slice(0, 180),
  attachment_count: msg.attachments?.length || 0,
});

const cleanLinkHref = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '#';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const isInteractiveMessageTarget = (target) => Boolean(
  target?.closest?.('a[data-chat-link],button,input,textarea,[data-reaction-tray],[data-message-controls],[data-message-actions]')
);

const openChatLink = (event, href) => {
  event.stopPropagation();
  event.preventDefault();
  const opened = window.open(href, '_blank', 'noopener,noreferrer');
  if (!opened) window.location.assign(href);
};

const renderLinkedMessageText = (text, isMe) => {
  const parts = [];
  let lastIndex = 0;
  String(text || '').replace(LINK_RE, (match, _url, offset) => {
    if (offset > lastIndex) parts.push(String(text).slice(lastIndex, offset));
    parts.push({
      type: 'link',
      text: match,
      href: cleanLinkHref(match),
    });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < String(text || '').length) parts.push(String(text).slice(lastIndex));
  return parts.map((part, index) => (
    typeof part === 'string' ? part : (
      <a
        key={`${part.href}-${index}`}
        data-chat-link
        href={part.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => openChatLink(e, part.href)}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="font-extrabold underline decoration-2 underline-offset-2"
        style={{ color: isMe ? '#FFFFFF' : '#0057D9' }}
      >
        {part.text}
      </a>
    )
  ));
};

const withAgentTag = (body, agentName) => {
  if (!agentName) return body;
  return `${AGENT_SUBJECT_PREFIX}${agentName}${body ? `\n\n${body}` : ''}`;
};

const messageBelongsToAgent = (msg, agent, fallbackConversation) => {
  if (!agent?.id && !agent?.name) return true;
  const parsed = parseAgentTagFromBody(msg?.body || '');
  return sameChatAgent(agent, {
    id: msg?.target_agent_id,
    name: msg?.target_agent_name || parsed.agentName || fallbackConversation?.preferred_agent_name
      || agentNameFromSubject(fallbackConversation?.subject),
  });
};

export default function ChatPanel({ user, selectedAgent, onRead }) {
  const { theme } = useTenantTheme();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [activeReactionId, setActiveReactionId] = useState(null);
  const [expandedReactionId, setExpandedReactionId] = useState(null);
  const [activeMessageMenuId, setActiveMessageMenuId] = useState(null);
  const [messageMenuMode, setMessageMenuMode] = useState('controls');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [visibleTimeId, setVisibleTimeId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [agentTyping, setAgentTyping] = useState('');
  const inputRef = useRef(null);
  const photoRef = useRef(null);
  const wsRef = useRef(null);
  const onReadRef = useRef(onRead);
  const reconnectAllowedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const forceNextScrollRef = useRef(true);
  const userScrollingRef = useRef(false);
  const userScrollTimerRef = useRef(null);
  const ignoreProgrammaticScrollRef = useRef(false);
  const reconnectTimer = useRef(null);
  const readRefreshTimerRef = useRef(null);
  const lastReadAcknowledgementRef = useRef('');
  const readyReadStreamRef = useRef('');
  const reactionTimerRef = useRef(null);
  const messageSwipeRef = useRef(null);
  const swipeActivatedRef = useRef(false);
  const previewHistoryRef = useRef(false);
  const typingTimerRef = useRef(null);
  const typingPulseTimerRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const keyboardFocusTimerRef = useRef(null);
  const scrollPinTimersRef = useRef([]);
  const sendingRef = useRef(false);

  useEffect(() => {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    const html = document.documentElement;
    const previousBody = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    const previousHtml = {
      overflow: html.style.overflow,
      overscrollBehavior: html.style.overscrollBehavior,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';

    return () => {
      body.style.position = previousBody.position;
      body.style.top = previousBody.top;
      body.style.left = previousBody.left;
      body.style.right = previousBody.right;
      body.style.width = previousBody.width;
      body.style.overflow = previousBody.overflow;
      body.style.overscrollBehavior = previousBody.overscrollBehavior;
      html.style.overflow = previousHtml.overflow;
      html.style.overscrollBehavior = previousHtml.overscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const syncViewport = () => {
      const viewport = window.visualViewport;
      const height = Math.max(320, Math.round(viewport?.height || window.innerHeight));
      const top = Math.max(0, Math.round(viewport?.offsetTop || 0));
      document.documentElement.style.setProperty('--player-chat-viewport-height', `${height}px`);
      document.documentElement.style.setProperty('--player-chat-viewport-top', `${top}px`);
    };
    syncViewport();
    window.visualViewport?.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('scroll', syncViewport);
    window.addEventListener('resize', syncViewport);
    window.addEventListener('orientationchange', syncViewport);
    return () => {
      window.visualViewport?.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('scroll', syncViewport);
      window.removeEventListener('resize', syncViewport);
      window.removeEventListener('orientationchange', syncViewport);
      document.documentElement.style.removeProperty('--player-chat-viewport-height');
      document.documentElement.style.removeProperty('--player-chat-viewport-top');
    };
  }, []);

  useEffect(() => {
    onReadRef.current = onRead;
  }, [onRead]);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        setMessages([]);
        setLoading(true);
        readyReadStreamRef.current = '';
        userScrollingRef.current = false;
        isNearBottomRef.current = true;
        if (isLocalPlayerDemoChat()) {
          const room = demoConversationForAgent(selectedAgent);
          setConversation(room);
          setMessages(readDemoMessages(selectedAgent));
          setWsConnected(true);
          scrollToBottom(true);
          onReadRef.current?.();
          return;
        }
        const res = await liveChatApi.post('/api/chat/conversations', {
          subject: selectedAgent?.name ? `Texted to agent ${selectedAgent.name}` : 'Website live support',
          agent_id: selectedAgent?.id || null,
          agent_name: selectedAgent?.name || null,
        });
        if (cancelled) return;
        const room = res.data.conversation;
        forceNextScrollRef.current = true;
        setConversation(room);
        await refreshMessages(room.id, { cancelled: () => cancelled });
        scrollToBottom(true);
      } catch {
        toast.error('Live chat is not available right now');
      } finally {
        if (!cancelled) {
          setLoading(false);
          scrollToBottom(true);
        }
      }
    };
    boot();
    return () => { cancelled = true; };
  }, [selectedAgent?.id, selectedAgent?.name]);

  const refreshMessages = async (roomId, options = {}) => {
    try {
      if (isLocalPlayerDemoChat()) {
        setMessages(readDemoMessages(selectedAgent));
        scrollToBottom(true);
        onReadRef.current?.();
        return;
      }
      const params = new URLSearchParams({ limit: '60' });
      if (selectedAgent?.id) params.set('agent_id', selectedAgent.id);
      if (selectedAgent?.name) params.set('agent_name', selectedAgent.name);
      const scrollEl = messagesScrollRef.current;
      const preserveScroll = Boolean(
        scrollEl
        && !forceNextScrollRef.current
        && (userScrollingRef.current || !isChatNearBottom())
      );
      const previousScrollTop = scrollEl?.scrollTop || 0;
      const previousScrollHeight = scrollEl?.scrollHeight || 0;
      const history = await liveChatApi.get(`/api/chat/conversations/${roomId}/messages?${params.toString()}`);
      if (options.cancelled?.()) return;
      readyReadStreamRef.current = `${roomId}:${chatAgentIdentityKey(selectedAgent)}`;
      const serverMessages = (history.data.messages || []).filter(msg => messageBelongsToAgent(msg, selectedAgent, history.data.conversation || conversation));
      setMessages(prev => {
        const pending = prev.filter(item => String(item.id || '').startsWith('temp_'));
        const next = [...serverMessages, ...pending].slice(-60);
        return messageListSignature(prev) === messageListSignature(next) ? prev : next;
      });
      if (preserveScroll && scrollEl) {
        requestAnimationFrame(() => {
          const heightDelta = scrollEl.scrollHeight - previousScrollHeight;
          scrollEl.scrollTop = previousScrollTop + heightDelta;
        });
      } else if (forceNextScrollRef.current) {
        scrollToBottom(true);
      }
    } catch {
      if (!options.quiet) toast.error('Live chat is not available right now');
    }
  };

  const readRequest = buildPlayerReadAcknowledgement(messages, selectedAgent);
  const readSignature = readRequest ? JSON.stringify(readRequest) : '';
  useEffect(() => {
    if (loading || !conversation?.id || !readSignature || isLocalPlayerDemoChat()) return undefined;
    const stream = `${conversation.id}:${chatAgentIdentityKey(selectedAgent)}`;
    if (readyReadStreamRef.current !== stream) return undefined;
    const key = `${stream}:${readSignature}`;
    if (lastReadAcknowledgementRef.current === key) return undefined;
    let active = true;
    let inFlight = false;
    let retryTimer;
    let frame;
    const acknowledge = async () => {
      if (!active || inFlight || document.visibilityState === 'hidden' || readyReadStreamRef.current !== stream) return;
      const request = JSON.parse(readSignature);
      const rendered = new Set(Array.from(messagesScrollRef.current?.querySelectorAll('[data-chat-message-id]') || [])
        .map(node => node.getAttribute('data-chat-message-id')));
      if (!request.rendered_message_ids.every(id => rendered.has(id))) return;
      inFlight = true;
      try {
        const response = await liveChatApi.post(`/api/chat/conversations/${conversation.id}/read`, request);
        if (!active || response.data?.ok !== true) {
          if (active) retryTimer = setTimeout(() => schedule(), 5000);
          return;
        }
        lastReadAcknowledgementRef.current = key;
        const readIds = new Set(request.rendered_message_ids);
        setMessages(prev => prev.map(message => readIds.has(message.id) ? { ...message, read_by_player: true } : message));
        onReadRef.current?.();
      } catch {
        if (active) retryTimer = setTimeout(() => schedule(), 5000);
      } finally {
        inFlight = false;
      }
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => void acknowledge());
    };
    schedule();
    document.addEventListener('visibilitychange', schedule);
    return () => {
      active = false;
      clearTimeout(retryTimer);
      cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', schedule);
    };
  }, [conversation?.id, loading, readSignature, selectedAgent?.id, selectedAgent?.name]);

  const isChatNearBottom = (threshold = 8) => {
    const el = messagesScrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const updateScrollLock = () => {
    if (ignoreProgrammaticScrollRef.current) return;
    userScrollingRef.current = true;
    clearTimeout(userScrollTimerRef.current);
    userScrollTimerRef.current = setTimeout(() => {
      userScrollingRef.current = false;
      isNearBottomRef.current = isChatNearBottom();
    }, 900);
    isNearBottomRef.current = isChatNearBottom();
  };

  const pinMessagesToRecent = () => {
    const el = messagesScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const scrollToBottom = (force = false) => {
    if (!force && !forceNextScrollRef.current) return;
    const finishProgrammaticScroll = () => {
      isNearBottomRef.current = true;
      forceNextScrollRef.current = false;
      setTimeout(() => {
        ignoreProgrammaticScrollRef.current = false;
      }, 120);
    };

    scrollPinTimersRef.current.forEach(clearTimeout);
    scrollPinTimersRef.current = [];
    ignoreProgrammaticScrollRef.current = true;
    userScrollingRef.current = false;
    [0, 40, 120, 260, 520, 900].forEach((delay, index, steps) => {
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          pinMessagesToRecent();
          if (index === steps.length - 1) finishProgrammaticScroll();
        });
      }, delay);
      scrollPinTimersRef.current.push(timer);
    });
  };

  useEffect(() => {
    return () => {
      scrollPinTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (loading || !conversation?.id || messages.length === 0 || !forceNextScrollRef.current) return;
    scrollToBottom(true);
  }, [loading, conversation?.id, messages.length]);

  useEffect(() => {
    if (!conversation?.id) return undefined;
    reconnectAllowedRef.current = true;
    const connect = async () => {
      const token = getStoredSessionToken({ portal: 'player' });
      if (!reconnectAllowedRef.current || !token) return;
      if (isLocalPlayerDemoChat()) {
        setWsConnected(true);
        return;
      }
      let socketTicket = null;
      try {
        socketTicket = await createLiveChatSocketTicket({ kind: 'player', conversationId: conversation.id });
      } catch {
        if (reconnectAllowedRef.current) reconnectTimer.current = setTimeout(() => void connect(), 2500);
        return;
      }
      if (!reconnectAllowedRef.current || getStoredSessionToken({ portal: 'player' }) !== token) return;
      const socketAuth = centralChatTicketsEnabled
        ? `ticket=${encodeURIComponent(socketTicket)}`
        : `token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(liveChatSocketUrl(`/ws/chat?${socketAuth}&conversation_id=${encodeURIComponent(conversation.id)}`));
      ws.onopen = () => {
        setWsConnected(true);
        ws._ping = setInterval(() => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
        }, 25000);
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const msg = data.message;
          if ((data.type === 'message.created' || data.type === 'message.ack') && msg) {
            const belongsToSelectedAgent = messageBelongsToAgent(msg, selectedAgent, conversation);
            if (!belongsToSelectedAgent) return;
            setMessages(prev => mergeChatMessage(prev, msg, 60));
            // Refresh history for a canonical sequence; the rendered stream
            // sends an explicit read acknowledgement after paint.
            if (shouldRefreshReadForSocketMessage(data.type, msg, belongsToSelectedAgent)) {
              clearTimeout(readRefreshTimerRef.current);
              readRefreshTimerRef.current = setTimeout(() => {
                void refreshMessages(conversation.id, { quiet: true });
              }, 80);
            }
          }
          if (data.type === 'message.reaction' && msg) {
            setMessages(prev => prev.map(item => item.id === msg.id ? msg : item));
          }
          if ((data.type === 'message.updated' || data.type === 'message.deleted') && msg) {
            setMessages(prev => prev.map(item => item.id === msg.id ? msg : item));
          }
          if (data.type === 'typing' && data.sender_role === 'staff') {
            const name = data.agent_name || data.sender_name || selectedAgent?.name || conversation?.preferred_agent_name || 'Agent';
            setAgentTyping(data.is_typing ? name : '');
            if (data.is_typing) {
              clearTimeout(typingTimerRef.current);
              typingTimerRef.current = setTimeout(() => setAgentTyping(''), 3500);
            }
          }
          if (data.type === 'conversation.closed') {
            toast('Support closed this conversation');
          }
          if (data.type === 'conversation.read' && data.audience === 'player') {
            onReadRef.current?.();
          }
        } catch {}
      };
      ws.onclose = () => {
        setWsConnected(false);
        clearInterval(ws._ping);
        if (reconnectAllowedRef.current) {
          reconnectTimer.current = setTimeout(connect, 2500);
        }
      };
      ws.onerror = () => ws.close();
      wsRef.current = ws;
    };
    connect();
    return () => {
      reconnectAllowedRef.current = false;
      clearTimeout(reconnectTimer.current);
      clearTimeout(readRefreshTimerRef.current);
      clearTimeout(typingTimerRef.current);
      clearInterval(typingPulseTimerRef.current);
      clearTimeout(typingStopTimerRef.current);
      clearTimeout(keyboardFocusTimerRef.current);
      clearTimeout(userScrollTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [conversation?.id, selectedAgent?.id, selectedAgent?.name]);

  useEffect(() => {
    if (!conversation?.id || !isLocalPlayerDemoChat()) return undefined;
    const syncDemoChat = () => {
      setMessages(readDemoMessages(selectedAgent));
      syncDemoMessagesFromBridge(selectedAgent).then(setMessages);
      scrollToBottom(true);
    };
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(LOCAL_DEMO_CHAT_EVENT) : null;
    const onStorage = (event) => {
      if (!event || event.key === LOCAL_DEMO_CHAT_KEY) syncDemoChat();
    };
    window.addEventListener(LOCAL_DEMO_CHAT_EVENT, syncDemoChat);
    window.addEventListener('storage', onStorage);
    if (channel) channel.onmessage = syncDemoChat;
    const interval = setInterval(syncDemoChat, 500);
    return () => {
      window.removeEventListener(LOCAL_DEMO_CHAT_EVENT, syncDemoChat);
      window.removeEventListener('storage', onStorage);
      if (channel) channel.close();
      clearInterval(interval);
    };
  }, [conversation?.id, selectedAgent?.id, selectedAgent?.name]);

  useEffect(() => {
    if (!conversation?.id) return undefined;
    const interval = setInterval(() => {
      if (wsConnected) return;
      if (userScrollingRef.current || !isChatNearBottom()) return;
      refreshMessages(conversation.id, { quiet: true });
    }, wsConnected ? 6000 : 2500);
    return () => clearInterval(interval);
  }, [conversation?.id, wsConnected, selectedAgent?.id, selectedAgent?.name]);

  const latestMessageKey = messages.length
    ? `${messages[messages.length - 1]?.id || ''}:${messages[messages.length - 1]?.created_at || ''}:${messages[messages.length - 1]?.edited_at || ''}:${messages[messages.length - 1]?.deleted_at || ''}`
    : '';

  useEffect(() => {
    if (latestMessageKey) {
      scrollToBottom();
    }
  }, [latestMessageKey, messages.length]);

  useEffect(() => {
    const closeOnBack = () => {
      if (previewHistoryRef.current) {
        previewHistoryRef.current = false;
        setPhotoPreview(null);
      }
    };
    window.addEventListener('popstate', closeOnBack);
    return () => window.removeEventListener('popstate', closeOnBack);
  }, []);

  const uploadPhoto = async (selectedPhoto = photo) => {
    if (!selectedPhoto) return [];
    if (isLocalPlayerDemoChat()) return [];
    const form = new FormData();
    form.append('file', selectedPhoto);
    const res = await liveChatApi.post('/api/chat/uploads', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return [res.data.attachment.id];
  };

  const sendTypingState = (isTyping) => {
    if (isLocalPlayerDemoChat()) return;
    if (wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({
      type: 'typing',
      target_agent_id: selectedAgent?.id || conversation?.preferred_agent_id || null,
      target_agent_name: selectedAgent?.name || conversation?.preferred_agent_name || null,
      is_typing: Boolean(isTyping),
    }));
  };

  const stopTypingPulse = () => {
    clearInterval(typingPulseTimerRef.current);
    typingPulseTimerRef.current = null;
    sendTypingState(false);
  };

  const startTypingPulse = (value) => {
    if (!String(value || '').trim()) {
      stopTypingPulse();
      return;
    }
    sendTypingState(true);
    if (!typingPulseTimerRef.current) {
      typingPulseTimerRef.current = setInterval(() => sendTypingState(true), 850);
    }
    clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = setTimeout(stopTypingPulse, 1700);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInput(value);
    startTypingPulse(value);
  };

  const handleInputFocus = () => {
    clearTimeout(keyboardFocusTimerRef.current);
    keyboardFocusTimerRef.current = setTimeout(() => {
      forceNextScrollRef.current = true;
      scrollToBottom(true);
    }, 320);
  };

  const sendMessage = async () => {
    if (!conversation?.id || sendingRef.current) return;
    const body = input.trim();
    const selectedPhoto = photo;
    if (!body && !selectedPhoto) return;
    sendingRef.current = true;
    const photoPreviewUrl = selectedPhoto ? URL.createObjectURL(selectedPhoto) : '';
    const activeAgentForMessage = selectedAgent?.name || conversation?.preferred_agent_name || agentNameFromSubject(conversation?.subject) || '';
    const serverBody = withAgentTag(body, activeAgentForMessage);
    const activeReply = replyToMessage?.id ? replyToMessage : null;
    const clientMessageId = `player_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const tempId = `temp_${clientMessageId}`;
    const tempMessage = {
      id: tempId,
      client_message_id: clientMessageId,
      conversation_id: conversation.id,
      tenant_id: conversation.tenant_id,
      sender_id: user?.id,
      sender_name: user?.username || user?.email || 'You',
      sender_avatar_url: user?.avatar_url || null,
      sender_role: 'player',
      body,
      attachments: selectedPhoto ? [{ id: 'pending', filename: selectedPhoto.name, url: photoPreviewUrl, preview_url: photoPreviewUrl }] : [],
      reactions: {},
      created_at: new Date().toISOString(),
      read_by_player: true,
      read_by_staff: false,
      reply_to_message_id: activeReply?.id || null,
      reply_to: activeReply || null,
      target_agent_id: selectedAgent?.id || conversation?.preferred_agent_id || null,
      target_agent_name: activeAgentForMessage || null,
      target_agent_title: selectedAgent?.title || conversation?.preferred_agent_title || '',
      target_agent_avatar_url: selectedAgent?.avatar_url || conversation?.preferred_agent_avatar_url || '',
    };
    forceNextScrollRef.current = true;
    setMessages(prev => [...prev, tempMessage].slice(-60));
    setInput('');
    setReplyToMessage(null);
    stopTypingPulse();
    setPhoto(null);
    setEmojiOpen(false);
    if (photoRef.current) photoRef.current.value = '';
    inputRef.current?.focus();
    setSending(true);
    if (isLocalPlayerDemoChat()) {
      const instantMessage = {
        ...tempMessage,
        id: `demo-player-${Date.now()}`,
        read_by_staff: false,
        read_by_player: true,
      };
      const nextMessages = writeDemoMessages(prev => [...prev, instantMessage].slice(-60), selectedAgent);
      setMessages(nextMessages);
      sendingRef.current = false;
      setSending(false);
      return;
    }
    try {
      const attachmentIds = await uploadPhoto(selectedPhoto);
      const res = await liveChatApi.post(`/api/chat/conversations/${conversation.id}/messages`, {
        body: serverBody,
        attachment_ids: attachmentIds,
        client_message_id: clientMessageId,
        reply_to_message_id: activeReply?.id || null,
        target_agent_id: selectedAgent?.id || conversation?.preferred_agent_id || null,
        target_agent_name: activeAgentForMessage || null,
      });
      setMessages(prev => mergeChatMessage(prev, res.data.message, 60));
    } catch {
      setMessages(prev => prev.map(item => item.id === tempId ? { ...item, send_failed: true } : item));
      toast.error('Failed to send live chat message');
    } finally {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
      sendingRef.current = false;
      setSending(false);
    }
  };

  const addEmoji = (emoji) => {
    setInput(value => `${value}${emoji}`);
    inputRef.current?.focus();
  };

  const reactToMessage = async (messageId, emoji) => {
    try {
      const res = await liveChatApi.post(`/api/chat/messages/${messageId}/reactions`, { emoji });
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      closeMessageMenu();
    } catch {
      toast.error('Could not add reaction');
    }
  };

  const closeMessageMenu = () => {
    clearTimeout(reactionTimerRef.current);
    setActiveReactionId(null);
    setExpandedReactionId(null);
    setActiveMessageMenuId(null);
    setMessageMenuMode('controls');
    setEditingMessageId(null);
    setEditingText('');
  };

  const openMessageMenu = (msg) => {
    if (String(msg.id || '').startsWith('temp_')) return;
    setActiveMessageMenuId(msg.id);
    setActiveReactionId(null);
    setExpandedReactionId(null);
    setMessageMenuMode('controls');
    setVisibleTimeId(msg.id);
    setEditingMessageId(null);
    setEditingText('');
  };

  const copyMessageText = async (msg) => {
    const text = parseAgentTagFromBody(msg.body || '').body.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
      closeMessageMenu();
    } catch {
      toast.error('Copy failed');
    }
  };

  const startEditingMessage = (msg) => {
    setEditingMessageId(msg.id);
    setEditingText(parseAgentTagFromBody(msg.body || '').body);
  };

  const saveEditedMessage = async (messageId) => {
    const body = editingText.trim();
    if (!body) return;
    try {
      const original = messages.find(item => item.id === messageId);
      const originalAgentName = parseAgentTagFromBody(original?.body || '').agentName;
      const res = await liveChatApi.patch(`/api/chat/messages/${messageId}`, { body: withAgentTag(body, originalAgentName || activeAgentName) });
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      closeMessageMenu();
    } catch {
      toast.error('Could not edit message');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      const res = await liveChatApi.delete(`/api/chat/messages/${messageId}`);
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      closeMessageMenu();
    } catch {
      toast.error('Could not delete message');
    }
  };

  const startReplyToMessage = (msg) => {
    if (!msg?.id || String(msg.id).startsWith('temp_') || msg.deleted) return;
    setReplyToMessage(replyPreviewForMessage(msg));
    closeMessageMenu();
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const beginMessageSwipe = (x, y, msg, event) => {
    if (isInteractiveMessageTarget(event?.target)) return;
    beginLongPress(msg.id);
    messageSwipeRef.current = {
      id: msg.id,
      x,
      y,
    };
  };

  const endMessageSwipe = (x, y, msg, event) => {
    cancelLongPress();
    const start = messageSwipeRef.current;
    messageSwipeRef.current = null;
    if (!start || start.id !== msg.id) return;
    const deltaX = x - start.x;
    const deltaY = Math.abs(y - start.y);
    if (Math.abs(deltaX) > 42 && deltaY < 38) {
      event.preventDefault();
      swipeActivatedRef.current = true;
      startReplyToMessage(msg);
      setTimeout(() => {
        swipeActivatedRef.current = false;
      }, 250);
    }
  };

  const handleMessageTouchStart = (event, msg) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    beginMessageSwipe(touch.clientX, touch.clientY, msg, event);
  };

  const handleMessageTouchEnd = (event, msg) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    endMessageSwipe(touch.clientX, touch.clientY, msg, event);
  };

  const handleMessagePointerDown = (event, msg) => {
    if (event.pointerType === 'mouse' || isInteractiveMessageTarget(event.target)) return;
    beginMessageSwipe(event.clientX, event.clientY, msg, event);
  };

  const handleMessagePointerUp = (event, msg) => {
    if (event.pointerType === 'mouse' || isInteractiveMessageTarget(event.target)) return;
    endMessageSwipe(event.clientX, event.clientY, msg, event);
  };

  const beginLongPress = (messageId) => {
    clearTimeout(reactionTimerRef.current);
    reactionTimerRef.current = setTimeout(() => {
      setActiveMessageMenuId(messageId);
      setActiveReactionId(null);
      setMessageMenuMode('controls');
      setVisibleTimeId(messageId);
    }, 450);
  };

  const cancelLongPress = () => {
    clearTimeout(reactionTimerRef.current);
  };

  const closeReactionsOnOutsideClick = (event) => {
    if (!activeReactionId && !activeMessageMenuId) return;
    const target = event.target;
    if (target?.closest?.('[data-chat-bubble], [data-reaction-tray], [data-message-controls], [data-message-actions]')) return;
    closeMessageMenu();
  };

  const openPhotoPreview = (item) => {
    setPhotoPreview(item);
    if (!previewHistoryRef.current) {
      window.history.pushState({ chatPhotoPreview: true }, '');
      previewHistoryRef.current = true;
    }
  };

  const closePhotoPreview = () => {
    if (previewHistoryRef.current) {
      previewHistoryRef.current = false;
      window.history.replaceState({ ...(window.history.state || {}), chatPhotoPreview: false }, '');
    }
    setPhotoPreview(null);
  };

  const deliveryStatusForMessage = (msg, isMe) => {
    if (!isMe) return null;
    if (String(msg.id || '').startsWith('temp_')) return msg.send_failed ? 'Failed' : 'Sending';
    return msg.read_by_staff ? 'Seen' : 'Delivered';
  };

  const renderReactionBadges = (msg, isMe) => {
    const entries = Object.entries(msg.reactions || {}).filter(([, users]) => users?.length).slice(-1);
    if (!entries.length) return null;
    return (
      <div className="absolute -bottom-2 -right-2 z-10 flex flex-wrap gap-1">
        {entries.map(([emoji, users]) => (
          <button
            key={emoji}
            type="button"
            onClick={() => reactToMessage(msg.id, emoji)}
            className="h-5 min-w-5 px-1 rounded-full text-[13px] flex items-center justify-center gap-0.5"
            style={{
              background: isMe ? 'rgba(0,132,255,0.82)' : 'rgba(15,23,42,0.10)',
              color: isMe ? '#FFFFFF' : '#111827',
              boxShadow: 'none',
              border: 'none',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif'
            }}
          >
            <span>{emoji}</span>
            {users.length > 1 && <span className="text-[9px] opacity-75">{users.length}</span>}
          </button>
        ))}
      </div>
    );
  };

  const renderQuickReactions = (msg) => (
    activeMessageMenuId === msg.id && messageMenuMode === 'emoji' ? (
    <div className="mb-1 flex flex-col items-start gap-1.5" data-reaction-tray>
      <div className="flex gap-1 opacity-95 rounded-full bg-white px-1.5 py-1 shadow-xl">
        {QUICK_REACTIONS.map(emoji => (
          <button
            key={emoji}
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={() => reactToMessage(msg.id, emoji)}
            className="w-7 h-7 rounded-full text-[14px] transition hover:scale-110"
            style={{ color: '#111827', fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
            aria-label={`React ${emoji}`}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={() => setExpandedReactionId(current => current === msg.id ? null : msg.id)}
          className="w-7 h-7 rounded-full text-[18px] leading-none transition hover:bg-black/5"
          style={{ color: '#111827' }}
          aria-label="More reactions"
        >
          +
        </button>
      </div>
      {expandedReactionId === msg.id && (
        <div className="grid max-h-32 w-56 grid-cols-7 gap-0.5 overflow-y-auto rounded-2xl bg-white p-1.5 shadow-2xl">
          {IPHONE_EMOJIS.map((emoji, index) => (
            <button
              key={`${emoji}-${index}`}
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={() => reactToMessage(msg.id, emoji)}
              className="h-7 rounded-xl text-[18px] transition hover:bg-black/5"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
              aria-label={`React ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
    ) : null
  );

  const renderMessageControls = (msg, isMe) => {
    if (activeMessageMenuId !== msg.id || messageMenuMode !== 'controls' || msg.deleted) return null;
    return (
      <div
        className={`mt-1 flex items-center gap-1.5 ${isMe ? 'justify-end pr-3' : 'justify-start pl-3'}`}
        data-message-controls
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            setActiveReactionId(msg.id);
            setMessageMenuMode('emoji');
            setExpandedReactionId(null);
          }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow-xl transition hover:scale-105"
          style={{ width: 36, height: 36, minWidth: 36, minHeight: 36, padding: 0, borderRadius: 9999, background: 'rgba(10,10,16,0.86)', border: '1px solid rgba(255,255,255,0.18)' }}
          aria-label="Open reactions"
        >
          <SmilePlus className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveReactionId(null);
            setExpandedReactionId(null);
            setMessageMenuMode('actions');
          }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow-xl transition hover:scale-105"
          style={{ width: 36, height: 36, minWidth: 36, minHeight: 36, padding: 0, borderRadius: 9999, background: 'rgba(10,10,16,0.86)', border: '1px solid rgba(255,255,255,0.18)' }}
          aria-label="Open message actions"
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </div>
    );
  };

  const renderMessageActions = (msg, isMe) => {
    if (activeMessageMenuId !== msg.id || messageMenuMode !== 'actions' || msg.deleted) return null;
    const displayBody = parseAgentTagFromBody(msg.body || '').body;
    const canEdit = isMe && Boolean(displayBody.trim()) && !String(msg.id || '').startsWith('temp_');
    const canDelete = isMe && !String(msg.id || '').startsWith('temp_');
    const canCopy = Boolean(displayBody.trim());
    return (
      <div
        className={`mt-1 flex flex-col gap-1 ${isMe ? 'items-end' : 'items-start'}`}
        data-message-actions
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="w-40 overflow-hidden rounded-2xl bg-white py-1 shadow-xl">
          {canDelete && (
            <button
              type="button"
              onClick={() => deleteMessage(msg.id)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={() => startReplyToMessage(msg)}
            className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
          >
            <Reply className="h-4 w-4" />
            Reply
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => startEditingMessage(msg)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          )}
          {canCopy && (
            <button
              type="button"
              onClick={() => copyMessageText(msg)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
            >
              <Copy className="h-4 w-4" />
              Copy text
            </button>
          )}
        </div>
        {editingMessageId === msg.id && (
          <div className="flex w-[min(260px,78vw)] items-center gap-1 rounded-2xl bg-white p-1.5 shadow-xl">
            <input
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveEditedMessage(msg.id);
                }
                if (e.key === 'Escape') closeMessageMenu();
              }}
              className="min-w-0 flex-1 rounded-full bg-gray-100 px-3 py-2 text-[13px] text-gray-950 outline-none"
              autoFocus
            />
            <button
              type="button"
              onClick={() => saveEditedMessage(msg.id)}
              className="h-8 rounded-full bg-[#006B85] px-3 text-[11px] font-bold text-white"
            >
              Save
            </button>
            <button type="button" onClick={closeMessageMenu} className="h-8 w-8 rounded-full text-gray-500 hover:bg-gray-100" aria-label="Cancel edit">
              <X className="mx-auto h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderAttachments = (attachments = [], imageOnly = false) => {
    if (!attachments.length) return null;
    return (
      <div className={`${imageOnly ? '' : 'mt-2'} flex flex-wrap gap-2`}>
        {attachments.map(item => {
          const imageUrl = resolveMediaUrl(item.url || item.preview_url || (item.path ? `/uploads/${item.path}` : ''));
          if (!imageUrl) return null;
          return (
            <button key={item.id} type="button" onClick={() => openPhotoPreview({ ...item, url: imageUrl })} className="block text-left">
              <SafeImage src={imageUrl} alt={item.filename || 'chat upload'} className="max-w-[150px] max-h-[150px] object-cover rounded-xl" loading="lazy" fallback={null} />
            </button>
          );
        })}
      </div>
    );
  };

  const renderReplyPreview = (reply, isMe) => {
    if (!reply?.id) return null;
    const preview = reply.body || (reply.attachment_count ? 'Photo' : 'Message');
    return (
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        className="mb-1.5 block max-w-full rounded-xl border-l-4 px-2.5 py-1.5 text-left"
        style={{
          background: isMe ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.07)',
          borderLeftColor: isMe ? 'rgba(255,255,255,0.72)' : '#006B85',
          color: isMe ? '#EAF2FF' : '#334155',
        }}
      >
        <span className="block truncate text-[11px] font-black">
          {reply.sender_role === 'player' ? 'Player' : (reply.sender_name || 'Support')}
        </span>
        <span className="block truncate text-[12px] font-semibold opacity-80">
          {preview}
        </span>
      </button>
    );
  };

  const brandAvatarUrl = resolveMediaUrl(theme?.logo_url || theme?.intro_logo_url || conversation?.tenant_logo_url || '');
  const activeAgentName = selectedAgent?.name || conversation?.preferred_agent_name || agentNameFromSubject(conversation?.subject) || '';
  const activeAgentTitle = selectedAgent?.title || conversation?.preferred_agent_title || 'Support Agent';
  const activeAgentSubtitle = chatSubtitleForAgent(activeAgentName, activeAgentTitle);
  const activeAgentAvatarUrl = resolveMediaUrl(selectedAgent?.avatar_url || conversation?.preferred_agent_avatar_url || '');
  const avatarForMessage = (msg, isMe) => {
    if (isMe) return resolveMediaUrl(user?.avatar_url || brandAvatarUrl);
    return resolveMediaUrl(msg.target_agent_avatar_url || activeAgentAvatarUrl || msg.sender_avatar_url || conversation?.tenant_logo_url || brandAvatarUrl);
  };

  const renderAvatar = (url, fallback, className = 'w-7 h-7') => (
    <div className={`${className} rounded-full shrink-0 flex items-center justify-center text-[10px] font-black overflow-hidden`} style={{ background: '#fff', color: '#111827' }}>
      {url ? <SafeImage src={url} alt="" className="w-full h-full object-cover" loading="lazy" fallback={fallback} /> : fallback}
    </div>
  );

  const renderDateSeparator = (label) => label ? (
    <div className="flex justify-center py-5">
      <span
        className="rounded-full px-3.5 py-1 text-[12px] font-semibold"
        style={{
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.10)',
          color: '#B7BFCC',
        }}
      >
        {label}
      </span>
    </div>
  ) : null;

  return (
    <div
      className="relative flex h-full min-h-0 flex-col overflow-hidden overscroll-none"
      data-testid="chat-panel"
      onPointerDownCapture={closeReactionsOnOutsideClick}
      style={{
        background: 'var(--vl-bg, #000000)',
      }}
    >
      <SheetHeader
        className="sticky top-0 z-30 shrink-0 px-5 pr-20 pb-3 border-b"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)',
          borderColor: 'rgba(255,255,255,0.10)',
          background: 'var(--vl-bg, #000000)',
          boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="relative h-12 w-12 shrink-0 overflow-visible rounded-full"
            style={{ filter: 'drop-shadow(0 0 14px rgba(0,132,255,0.22))' }}
          >
            <div className="grid h-12 w-12 place-items-center overflow-hidden rounded-full bg-white text-sm font-black text-gray-950">
              {activeAgentAvatarUrl ? (
                <SafeImage src={activeAgentAvatarUrl} alt="" className="h-full w-full object-cover" loading="lazy" fallback={(activeAgentName || 'S').charAt(0).toUpperCase()} />
              ) : (
                (activeAgentName || 'S').charAt(0).toUpperCase()
              )}
            </div>
            <span
              className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 bg-[#22C55E]"
              style={{ borderColor: '#0B3D2A', boxShadow: '0 0 12px rgba(34,197,94,0.95)' }}
              aria-label="Agent online"
            />
          </div>
          <div className="min-w-0 text-left">
            <SheetTitle className="display truncate text-xl font-extrabold" style={{ color: '#fff' }}>{activeAgentName || 'Live Support'}</SheetTitle>
            {activeAgentSubtitle && (
              <p className="mt-0.5 truncate text-xs uppercase tracking-[0.08em]" style={{ color: '#D7EFF3' }}>
                {activeAgentSubtitle}
              </p>
            )}
          </div>
        </div>
      </SheetHeader>

      <div
        ref={messagesScrollRef}
        onScroll={updateScrollLock}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-1"
        style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', overflowAnchor: 'none' }}
      >
        {loading ? (
          <div className="flex justify-center py-10"><div className="spinner" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-60" style={{ color: NEON }} />
            <p className="display font-semibold" style={{ color: '#fff' }}>Start a conversation</p>
            <p className="text-sm mt-1" style={{ color: '#D7EFF3' }}>
              {activeAgentName ? `Message will be delivered to ${activeAgentName}.` : 'Message will be delivered to support.'}
            </p>
          </div>
        ) : messages.map((msg, index) => {
          const isMe = msg.sender_role === 'player' || msg.sender_id === user?.id;
          const attachments = msg.attachments || [];
          const parsedBody = parseAgentTagFromBody(msg.body || '');
          const displayBody = parsedBody.body;
          const imageOnly = attachments.length > 0 && !displayBody;
          const avatarUrl = avatarForMessage(msg, isMe);
          const latestOwnMessageId = [...messages].reverse().find(item => item.sender_role === 'player' || item.sender_id === user?.id)?.id;
          const isLatestOwnMessage = isMe && msg.id === latestOwnMessageId;
          const messageStatus = deliveryStatusForMessage(msg, isMe);
          const separator = dateSeparatorLabel(msg.created_at, messages[index - 1]?.created_at);
          return (
            <div key={msg.id} className="contents">
              {renderDateSeparator(separator)}
              <div data-chat-message-id={msg.id} className={`mt-2.5 flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                {!isMe && renderAvatar(avatarUrl, (msg.sender_name || 'S').charAt(0).toUpperCase())}
                <div className={`max-w-[min(78%,420px)] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                  {renderQuickReactions(msg)}
                  {visibleTimeId === msg.id && (
                    <p className="mb-1 px-1 text-[10px]" style={{ color: '#C6CEDA' }}>
                      {formatChatTime(msg.created_at)}
                      {messageStatus && ` · ${messageStatus}`}
                      {msg.edited_at && !msg.deleted && ' · Edited'}
                    </p>
                  )}
                  <div className="relative w-fit max-w-full">
                    <div
                      data-chat-bubble
                      className="w-fit max-w-full select-none"
                      onMouseDown={() => beginLongPress(msg.id)}
                      onMouseUp={cancelLongPress}
                      onMouseLeave={cancelLongPress}
                      onPointerDown={(e) => handleMessagePointerDown(e, msg)}
                      onPointerUp={(e) => handleMessagePointerUp(e, msg)}
                      onTouchStart={(e) => handleMessageTouchStart(e, msg)}
                      onTouchEnd={(e) => handleMessageTouchEnd(e, msg)}
                      onClick={(e) => {
                        if (isInteractiveMessageTarget(e.target)) return;
                        if (swipeActivatedRef.current) {
                          swipeActivatedRef.current = false;
                          return;
                        }
                        openMessageMenu(msg);
                      }}
                      onContextMenu={(e) => { e.preventDefault(); openMessageMenu(msg); }}
                      style={{
                        background: imageOnly ? 'transparent' : (isMe ? '#006B85' : '#FFFFFF'),
                        color: isMe ? '#FFFFFF' : '#111827',
                        borderRadius: imageOnly ? 0 : (isMe ? '18px 18px 7px 18px' : '18px 18px 18px 7px'),
                        border: 'none',
                        boxShadow: imageOnly ? 'none' : (isMe ? '0 1px 0 rgba(255,255,255,0.16) inset, 0 7px 18px rgba(0,77,97,0.18)' : 'none'),
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        touchAction: 'pan-y',
                        ...(isMe && !imageOnly ? { background: 'linear-gradient(180deg, #075F75 0%, #004D61 100%)' } : {})
                      }}
                    >
                      <div className={imageOnly ? 'p-0' : 'px-3.5 py-1.5'}>
	                        {msg.deleted ? (
	                          <p className="text-[14px] italic opacity-70">Message deleted</p>
	                        ) : displayBody && (
                            <>
                              {renderReplyPreview(msg.reply_to, isMe)}
                              <p
                                className="text-[16px] font-medium leading-[1.32] whitespace-pre-wrap"
                                style={{ maxWidth: '31ch', overflowWrap: 'break-word', wordBreak: 'normal', hyphens: 'manual', textWrap: 'pretty', letterSpacing: 0 }}
                              >
                                {renderLinkedMessageText(displayBody, isMe)}
                              </p>
                            </>
	                        )}
                        {!msg.deleted && !displayBody && renderReplyPreview(msg.reply_to, isMe)}
                        {!msg.deleted && renderAttachments(attachments, imageOnly)}
                      </div>
                    </div>
                    {!msg.deleted && renderReactionBadges(msg, isMe)}
                  </div>
                  {renderMessageControls(msg, isMe)}
                  {renderMessageActions(msg, isMe)}
                  {isLatestOwnMessage && visibleTimeId !== msg.id && messageStatus && (
                    <p className="mt-0.5 px-1 text-[10px]" style={{ color: '#C6CEDA' }}>
                      {messageStatus}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {agentTyping && (
        <div className="shrink-0 px-4 pb-2">
          <div className="flex items-center gap-2">
            {renderAvatar(activeAgentAvatarUrl, (agentTyping || 'A').charAt(0).toUpperCase(), 'w-6 h-6')}
            <div className="rounded-full bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-700 shadow-sm">
              {agentTyping} is typing...
            </div>
          </div>
        </div>
      )}

      {replyToMessage && (
        <div className="shrink-0 px-4 py-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
          <div className="flex items-center gap-2 rounded-2xl bg-white/10 px-3 py-2">
            <Reply className="h-4 w-4 shrink-0" style={{ color: '#60A5FA' }} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] font-black uppercase tracking-[0.08em]" style={{ color: '#9CCBFF' }}>
                Replying to {replyToMessage.sender_role === 'player' ? 'you' : (replyToMessage.sender_name || 'support')}
              </p>
              <p className="truncate text-xs font-semibold" style={{ color: '#E5E7EB' }}>
                {replyToMessage.body || (replyToMessage.attachment_count ? 'Photo' : 'Message')}
              </p>
            </div>
            <button type="button" onClick={() => setReplyToMessage(null)} className="h-8 w-8 rounded-full text-gray-300 hover:bg-white/10" aria-label="Cancel reply">
              <X className="mx-auto h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {photo && (
        <div className="px-4 py-2 border-t flex items-center gap-2 text-xs" style={{ borderColor: 'rgba(255,255,255,0.10)', color: '#C6CEDA' }}>
          <ImageIcon className="w-4 h-4" />
          <span className="flex-1 truncate">{photo.name}</span>
          <button onClick={() => { setPhoto(null); if (photoRef.current) photoRef.current.value = ''; }} aria-label="Remove photo">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {emojiOpen && (
        <div className="px-4 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
          <div className="grid max-h-36 w-64 grid-cols-7 gap-0.5 overflow-y-auto rounded-2xl p-1.5" style={{ background: 'rgba(245,245,247,0.96)', boxShadow: '0 -10px 30px rgba(0,0,0,0.2)' }}>
            {IPHONE_EMOJIS.map((emoji, index) => (
              <button
                key={`${emoji}-${index}`}
                type="button"
                onClick={() => addEmoji(emoji)}
                className="h-7 rounded-xl text-[18px] hover:bg-black/5 transition"
                style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className="shrink-0 px-4 pt-4 border-t"
        style={{
          borderColor: 'rgba(255,255,255,0.10)',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex items-center gap-2 rounded-full px-2 py-2" style={{ background: 'rgba(8,14,20,0.86)', border: '1px solid rgba(255,255,255,0.10)' }}>
        <input
          ref={photoRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={e => setPhoto(e.target.files?.[0] || null)}
          data-testid="player-chat-photo"
        />
        <Button type="button" onClick={() => photoRef.current?.click()} variant="outline" className="h-10 w-10 p-0 rounded-full" style={{ borderColor: 'rgba(255,255,255,0.12)', color: '#fff', background: 'rgba(255,255,255,0.06)' }}>
          <Camera className="w-4 h-4" />
        </Button>
        <Button type="button" onClick={() => setEmojiOpen(open => !open)} variant="outline" className="h-10 w-10 p-0 rounded-full" style={{ borderColor: 'rgba(255,255,255,0.12)', color: '#fff', background: 'rgba(255,255,255,0.06)' }} aria-label="Open emoji picker">
          <SmilePlus className="w-4 h-4" />
        </Button>
        <Input
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={stopTypingPulse}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type a message..."
          className="chat-composer-input h-10 flex-1 rounded-full border-transparent px-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          style={{ background: 'transparent', borderColor: 'transparent', color: '#fff', boxShadow: 'none' }}
          data-chat-input="composer"
          data-testid="player-chat-input"
        />
        <Button onClick={sendMessage} disabled={(!input.trim() && !photo) || sending} className="h-10 w-11 p-0 rounded-full text-white" style={{ background: '#006B85', boxShadow: 'none' }} data-testid="player-chat-send">
          <Send className="w-4 h-4" />
        </Button>
        </div>
      </div>

      {photoPreview && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: 'rgba(3,8,18,0.72)', backdropFilter: 'blur(12px)' }}
          onClick={closePhotoPreview}
        >
          <div
            className="relative w-full max-w-3xl max-h-[86vh] rounded-2xl p-3"
            style={{ background: '#0F1B2A', border: '1px solid rgba(255,255,255,0.18)', boxShadow: '0 24px 80px rgba(0,0,0,0.48)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closePhotoPreview}
              className="absolute -top-3 -right-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: '#FFFFFF', color: '#111827', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
              aria-label="Close photo preview"
            >
              <X className="w-5 h-5" />
            </button>
            <SafeImage
              src={photoPreview.url}
              alt={photoPreview.filename || 'chat photo preview'}
              className="w-full max-h-[78vh] object-contain rounded-xl"
              fallback={null}
            />
          </div>
        </div>
      )}
    </div>
  );
}
